import React from 'react';
import { VStack, HStack, Button, Alert, AlertIcon, Text, SimpleGrid, Spinner, Box, Divider, Tooltip } from '@chakra-ui/react';
import { useIntl } from 'react-intl';
import { Formik, Form, FormikHelpers } from 'formik';
import { useCustomFields } from '../../hooks/customFields';
import { CustomFieldFormValues, UserCustomFieldValue } from '../../types/customFields';
import CustomFieldInput from './CustomFieldInput';
import { Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverBody, PopoverArrow, PopoverCloseButton } from '@chakra-ui/react';
import { ChevronDownIcon, PlusSquareIcon } from '@chakra-ui/icons';

// Form component props
export interface CustomFieldsFormProps {
    userId: number;
    onSuccess?: () => void;
    onCancel?: () => void;
    containerRef?: React.MutableRefObject<null>;
}
/**
 * Main form component for editing user custom fields
 * Fetches available field definitions and user values, renders dynamic form
 */
export const CustomFieldsForm: React.FC<CustomFieldsFormProps> = ({ userId, onSuccess, onCancel, containerRef }) => {
    const intl = useIntl();

    // Use the master custom fields hook
    const {
        definitions,
        userFields,
        warnings,
        isLoading,
        isError,
        // error, // Error are set by formikHelpers on submission
        selectedFieldIds,
        availableFields,
        selectedDefinitions,
        formInitialValues,
        addField,
        removeField,
        prepareSubmissionData,
        updateFields,
        isUpdating,
        isDeleting,
    } = useCustomFields(userId, { onSuccess });

    // Form submission handler
    const handleSubmit = async (values: CustomFieldFormValues, formikHelpers: FormikHelpers<CustomFieldFormValues>) => {
        const { apiRequest, fieldsToDelete } = prepareSubmissionData(values);
        await updateFields(apiRequest, fieldsToDelete, formikHelpers);
    };

    if (isLoading || !definitions) {
        return (
            <Box p={6} textAlign="center">
                <VStack spacing={3}>
                    <Spinner size="lg" />
                    <Text>{intl.formatMessage({ id: 'customFields.loading' })}</Text>
                </VStack>
            </Box>
        );
    }

    if (isError) {
        return (
            <Alert status="error">
                <AlertIcon />
                <Text>{intl.formatMessage({ id: 'customFields.error' })}</Text>
            </Alert>
        );
    }

    if (definitions.length === 0) {
        return (
            <Alert status="info">
                <AlertIcon />
                <VStack align="start" spacing={1}>
                    <Text fontWeight="semibold">{intl.formatMessage({ id: 'customFields.noFields' })}</Text>
                    <Text fontSize="sm">{intl.formatMessage({ id: 'customFields.noFieldsDescription' })}</Text>
                </VStack>
            </Alert>
        );
    }

    return (
        <Box>
            {/* Show warnings if any */}
            {warnings.length > 0 && (
                <Alert status="warning" mb={4}>
                    <AlertIcon />
                    <VStack align="start" spacing={1}>
                        {warnings.map((warning, index) => (
                            <Text key={index} fontSize="sm">
                                {warning}
                            </Text>
                        ))}
                    </VStack>
                </Alert>
            )}

            <Formik initialValues={formInitialValues} enableReinitialize={true} onSubmit={handleSubmit}>
                {({ values, setFieldValue, errors, isSubmitting, setFieldTouched }) => {
                    const isFormSubmitting = isSubmitting || isUpdating || isDeleting;

                    return (
                        <Form>
                            <VStack spacing={6} align="stretch">
                                {/* Add Field Selector */}
                                <HStack spacing={3}>
                                    <AddFieldPopover
                                        availableFields={availableFields}
                                        onAdd={(id) => addField(id)}
                                        isDisabled={isFormSubmitting}
                                    />
                                    <Text fontSize="sm" color="gray.500">
                                        {selectedFieldIds.length} / {definitions.length}
                                    </Text>
                                </HStack>
                                <Divider />
                                {/* Form Fields - only selected */}
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                                    {selectedDefinitions.map((definition) => {
                                        const fieldKey = definition.id.toString();
                                        const userField = userFields.find((uf) => uf.fieldName === definition.name);

                                        // Create field object for CustomFieldInput
                                        const field = {
                                            id: definition.id,
                                            fieldName: definition.name,
                                            displayName: definition.displayName,
                                            description: definition.description,
                                            fieldType: definition.fieldType,
                                            validationRules: definition.validationRules,
                                            selectOptions: definition.selectOptions,
                                            ...userField, // Merge with user field data if exists
                                        };

                                        return (
                                            <VStack key={definition.id} align="stretch" spacing={1}>
                                                <CustomFieldInput
                                                    field={field as UserCustomFieldValue}
                                                    value={values[fieldKey]}
                                                    onChange={(value) => {
                                                        setFieldValue(fieldKey, value);
                                                        // Mark field as touched when changed to show errors
                                                        setFieldTouched(fieldKey, true, false);
                                                    }}
                                                    error={errors[fieldKey] ? String(errors[fieldKey]) : undefined}
                                                    isDisabled={isFormSubmitting}
                                                    containerRef={containerRef}
                                                />

                                                <HStack justify="flex-end">
                                                    <Tooltip label={intl.formatMessage({ id: 'customFields.removeField' })}>
                                                        <Button
                                                            size="xs"
                                                            variant="ghost"
                                                            colorScheme="red"
                                                            onClick={() => removeField(definition.id)}
                                                        >
                                                            {intl.formatMessage({ id: 'customFields.removeField' })}
                                                        </Button>
                                                    </Tooltip>
                                                </HStack>
                                            </VStack>
                                        );
                                    })}
                                </SimpleGrid>
                                {/* Form Actions */}
                                <HStack spacing={3} justify="flex-end" pt={4}>
                                    {onCancel && (
                                        <Button variant="ghost" onClick={onCancel} isDisabled={isFormSubmitting}>
                                            {intl.formatMessage({ id: 'customFields.cancel' })}
                                        </Button>
                                    )}

                                    <Button
                                        type="submit"
                                        colorScheme="primary"
                                        isLoading={isFormSubmitting}
                                        loadingText={intl.formatMessage({ id: 'customFields.saving' })}
                                    >
                                        {intl.formatMessage({ id: 'customFields.save' })}
                                    </Button>
                                </HStack>
                            </VStack>
                        </Form>
                    );
                }}
            </Formik>
        </Box>
    );
};

// Inline component: Popover to add field
interface AddFieldPopoverProps {
    availableFields: Array<{ id: number; displayName: string }>;
    onAdd: (id: number) => void;
    isDisabled?: boolean;
}

const AddFieldPopover: React.FC<AddFieldPopoverProps> = ({ availableFields, onAdd, isDisabled }) => {
    const intl = useIntl();
    const hasOptions = availableFields.length > 0;

    return (
        <Popover placement="bottom-start">
            <PopoverTrigger>
                <Button leftIcon={<PlusSquareIcon />} size="sm" rightIcon={<ChevronDownIcon />} isDisabled={!hasOptions || isDisabled}>
                    {intl.formatMessage({ id: 'customFields.addField' })}
                </Button>
            </PopoverTrigger>
            <PopoverContent w="sm">
                <PopoverArrow />
                <PopoverCloseButton />
                <PopoverHeader fontWeight="semibold">{intl.formatMessage({ id: 'customFields.addField' })}</PopoverHeader>
                <PopoverBody>
                    <VStack align="stretch" spacing={2}>
                        {hasOptions ? (
                            availableFields.map((def) => (
                                <Button key={def.id} variant="ghost" justifyContent="flex-start" onClick={() => onAdd(def.id)}>
                                    {def.displayName}
                                </Button>
                            ))
                        ) : (
                            <Text fontSize="sm" color="gray.500">
                                {intl.formatMessage({ id: 'customFields.noFields' })}
                            </Text>
                        )}
                    </VStack>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
};

export default CustomFieldsForm;
